import React, { useEffect, useMemo, useState } from 'react';
import { ZendeskArticle, ZendeskArticleAttachment, ZendeskPost } from '../../../types/solution-center';
import LoadingSpinner, { LoadingSpinnerType } from '../../general-components/loading-spinner';
import { GetZendeskArticleAttachments } from '../../../connection/zendesk';

interface ContentViewProps {
	isLoading: boolean;
	contentResults: ZendeskArticle | ZendeskPost | null;
}

export default function ContentView(props: ContentViewProps) {
	const {
		isLoading,
		contentResults,
	} = props;

	const [attachments, setAttachments] = useState<ZendeskArticleAttachment[]>([]);

	useEffect(() => {
		if (contentResults && 'body' in contentResults) {
			GetZendeskArticleAttachments(contentResults.id).then((res) => {
				if (res.article_attachments) {
					setAttachments(res.article_attachments.filter((a) => !a.inline));
				}
			});
		}
	}, [contentResults]);

	const formatFileSize = (size: number) => {
		if (size < 1024) {
			return `${size} Bytes`;
		} if (size < 1048576) {
			return `${(size / 1024).toFixed(2)} KB`;
		} if (size < 1073741824) {
			return `${(size / 1048576).toFixed(2)} MB`;
		}
		return `${(size / 1073741824).toFixed(2)} GB`;
	};

	const attachmentItems = useMemo(() => attachments.map((attachment) => (
		<li key={`attachment-${attachment.id}`}>
			<a href={attachment.url} target="_blank">
				{attachment.file_name}
			</a>
			<span>({formatFileSize(attachment.size)})</span>
		</li>
	)), [attachments]);

	const articleBody = useMemo(() => {
		if (!contentResults) return '';
		// articles use the body property, while posts use the details property
		const html = 'body' in contentResults ? contentResults.body : contentResults.details;
		// update all links to open in new tab and use the correct property for the content type
		return html.replace(/<a /g, '<a rel="noopener noreferrer" target="_blank" ');
	}, [contentResults]);

	return (
		isLoading || !contentResults ? (
			<div className="sc-loading">
				<LoadingSpinner
					height={36}
					width={36}
					type={LoadingSpinnerType.Gradient}
					style={{ background: '#26242B' }}
				/>
			</div>
		) : (
			<>
				<h3 className="sc-h3">{contentResults.title}</h3>
				<span className="sc-c2-text date">
					{new Date(contentResults.updated_at).toLocaleDateString('en-US', {
						month: 'long',
						day: 'numeric',
						hour: '2-digit',
						minute: 'numeric',
					})}
				</span>
				<section className="sc-section-article section section-article" id="article-container">
					<div className="article">
						<div className="article-content">
							<div
								className="article-body"
								dangerouslySetInnerHTML={{
									__html: articleBody,
								}}
							/>
							{attachments.length > 0 && (
								<div className="article-attachments">
									<ul>
										{attachmentItems}
									</ul>
								</div>
							)}
						</div>
					</div>
				</section>
				<div className="sc-shadow" />
			</>
		)
	);
}
